/**
 * Ma marque — interactions du thème.
 * Tout est construit avec des éléments personnalisés : ils se réinitialisent
 * d'eux-mêmes quand l'éditeur Shopify recharge une section.
 * Sans JavaScript, le contenu reste entièrement visible et les formulaires
 * natifs Shopify fonctionnent (ajout au panier, contact, newsletter).
 */
(() => {
  'use strict';

  const theme = (window.theme = window.theme || {});
  theme.routes = theme.routes || {};
  theme.strings = theme.strings || {};
  theme.headerHeight = 0;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const inDesignMode = () => Boolean(window.Shopify && window.Shopify.designMode);

  const define = (name, constructor) => {
    if (!customElements.get(name)) customElements.define(name, constructor);
  };

  /* ---------------------------------------------------------------------
     Boutons : états « chargement » et « confirmation »
     Un petit témoin (roue ou coche) est inséré devant le libellé.
     --------------------------------------------------------------------- */
  theme.setButtonState = (button, state) => {
    if (!button) return;
    if (!state) {
      delete button.dataset.state;
      button.removeAttribute('aria-busy');
      return;
    }
    if (!button.querySelector('.btn__status')) {
      const status = document.createElement('span');
      status.className = 'btn__status';
      status.setAttribute('aria-hidden', 'true');
      button.prepend(status);
    }
    button.dataset.state = state;
    if (state === 'loading') button.setAttribute('aria-busy', 'true');
    else button.removeAttribute('aria-busy');
  };

  /* ---------------------------------------------------------------------
     Formulaires (contact, newsletter) : erreurs affichées près du champ
     Les formulaires marqués data-validate sont vérifiés avant l'envoi ; le
     message apparaît sous le champ concerné, qui reçoit le focus. Les valeurs
     saisies ne sont jamais effacées. L'envoi reste le formulaire natif Shopify.
     --------------------------------------------------------------------- */
  const fieldMessage = (field) => {
    const errors = theme.strings.errors || {};
    const validity = field.validity;
    if (validity.valueMissing) return errors.required || field.validationMessage;
    if (validity.typeMismatch && field.type === 'email') return errors.email || field.validationMessage;
    if (validity.typeMismatch || validity.patternMismatch) return errors.format || field.validationMessage;
    return field.validationMessage;
  };

  theme.showFieldError = (field, message) => {
    const target = field.id && field.form ? field.form.querySelector(`[data-error-for="${CSS.escape(field.id)}"]`) : null;
    if (message) field.setAttribute('aria-invalid', 'true');
    else field.removeAttribute('aria-invalid');
    if (!target) return;
    target.textContent = message;
    target.hidden = !message;
  };

  theme.validateForm = (form) => {
    let firstInvalid = null;
    Array.from(form.elements).forEach((field) => {
      if (!field.willValidate) return;
      if ((field instanceof HTMLTextAreaElement || field.type === 'text' || field.type === 'email') && !field.value.trim()) {
        field.value = '';
      }
      const valid = field.checkValidity();
      theme.showFieldError(field, valid ? '' : fieldMessage(field));
      if (!valid && !firstInvalid) firstInvalid = field;
    });
    if (firstInvalid) firstInvalid.focus();
    return !firstInvalid;
  };

  theme.initForms = (root = document) => {
    // Les bulles du navigateur sont remplacées par les messages près des champs
    root.querySelectorAll('form[data-validate]').forEach((form) => {
      form.noValidate = true;
    });
  };

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    if (form.hasAttribute('data-validate') && !theme.validateForm(form)) {
      event.preventDefault();
      return;
    }
    if (form.hasAttribute('data-product-form')) return;

    // Envoi natif accepté : le bouton passe en chargement pendant que la page suivante arrive
    const submitter = event.submitter instanceof Element ? event.submitter.closest('.btn') : null;
    const button = submitter || form.querySelector('button[type="submit"].btn');
    if (!button) return;
    setTimeout(() => {
      if (!event.defaultPrevented) theme.setButtonState(button, 'loading');
    }, 0);
  });

  document.addEventListener('input', (event) => {
    const field = event.target;
    if (!(field instanceof HTMLElement) || !field.form || !field.form.hasAttribute('data-validate')) return;
    if (field.getAttribute('aria-invalid') === 'true' && field.checkValidity()) theme.showFieldError(field, '');
  });

  /* Retour arrière du navigateur : on retire les états restés affichés */
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    document.querySelectorAll('.btn[data-state]').forEach((button) => theme.setButtonState(button, null));
  });

  /* ---------------------------------------------------------------------
     Fenêtres modales (<dialog>) : menu, recherche, panier, zoom, guide
     showModal() fournit nativement : fermeture par Échap, focus contenu dans
     la fenêtre, reste de la page inerte et retour du focus au déclencheur.
     --------------------------------------------------------------------- */
  theme.openDialog = (dialog) => {
    if (!dialog || dialog.open || typeof dialog.showModal !== 'function') return;
    dialog.showModal();
  };

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    // Lien vers une ancre de la page en cours, cliqué depuis une fenêtre
    // (menu mobile) : on ferme la fenêtre avant de défiler jusqu'à la cible.
    const anchor = target.closest('dialog[open] a[href*="#"]');
    if (anchor && anchor.hash.length > 1 && anchor.pathname === window.location.pathname && anchor.search === window.location.search) {
      let destination = null;
      try {
        destination = document.getElementById(decodeURIComponent(anchor.hash.slice(1)));
      } catch (error) {
        destination = null;
      }
      if (destination) {
        event.preventDefault();
        anchor.closest('dialog').close();
        window.history.pushState(null, '', anchor.hash);
        destination.scrollIntoView();
        return;
      }
    }

    const opener = target.closest('[data-dialog-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.dialogOpen);
      // Sans fenêtre disponible (ex. panier en mode « page »), le lien suit son href.
      if (dialog && typeof dialog.showModal === 'function') {
        event.preventDefault();
        theme.openDialog(dialog);
      }
      return;
    }

    const closer = target.closest('[data-dialog-close]');
    if (closer) {
      const dialog = closer.closest('dialog');
      if (dialog) dialog.close();
      return;
    }

    // Clic sur le fond assombri
    if (target instanceof HTMLDialogElement && target.open) {
      const rect = target.getBoundingClientRect();
      const inside =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;
      if (!inside) target.close();
    }

    // Menus déroulants : fermeture au clic extérieur
    document.querySelectorAll('details[data-dropdown][open]').forEach((details) => {
      if (!details.contains(target)) details.removeAttribute('open');
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !(event.target instanceof Element)) return;
    const details = event.target.closest('details[data-dropdown][open]');
    if (!details) return;
    details.removeAttribute('open');
    const summary = details.querySelector('summary');
    if (summary) summary.focus();
  });

  /* Envoi automatique des listes déroulantes (tri, pays, langue) */
  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement) || !target.hasAttribute('data-auto-submit') || !target.form) return;
    if (typeof target.form.requestSubmit === 'function') target.form.requestSubmit();
    else target.form.submit();
  });

  /* ---------------------------------------------------------------------
     Apparition au défilement — amélioration progressive
     Le CSS seul ne masque rien. Seuls les éléments encore sous l'écran sont
     masqués par ce script, puis révélés à leur arrivée. Rien n'est masqué si
     le script échoue, si les animations sont réduites ou dans l'éditeur.
     --------------------------------------------------------------------- */
  const reveal = (element) => {
    element.classList.add('reveal-run');
    element.classList.remove('reveal-pending');
    if (revealObserver) revealObserver.unobserve(element);
  };

  const revealObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) reveal(entry.target);
            });
          },
          { rootMargin: '0px 0px -6% 0px' }
        )
      : null;

  // Filet de sécurité : au défilement, tout élément en attente arrivé à l'écran
  // est révélé même si l'observateur ne s'est pas déclenché.
  const revealOnScroll = () => {
    const pending = document.querySelectorAll('.reveal-pending');
    if (!pending.length) {
      window.removeEventListener('scroll', revealOnScroll);
      return;
    }
    pending.forEach((element) => {
      if (element.getBoundingClientRect().top < window.innerHeight * 0.94) reveal(element);
    });
  };

  theme.initReveal = (root = document) => {
    if (!revealObserver || reduceMotion.matches || inDesignMode()) return;
    if (!document.body.classList.contains('reveal-on')) return;
    let added = false;
    root.querySelectorAll('[data-reveal]:not(.reveal-run):not(.reveal-pending)').forEach((element) => {
      if (element.getBoundingClientRect().top < window.innerHeight) return;
      element.classList.add('reveal-pending');
      revealObserver.observe(element);
      added = true;
    });
    if (added) window.addEventListener('scroll', revealOnScroll, { passive: true });
  };

  /* Place le focus sur le message de retour d'un formulaire après envoi */
  theme.focusFormFeedback = (root = document) => {
    const feedback = root.querySelector('[data-autofocus]');
    if (feedback) feedback.focus({ preventScroll: false });
  };

  /* ---------------------------------------------------------------------
     En-tête : hauteur exposée en variable CSS (ancres, éléments fixes)
     --------------------------------------------------------------------- */
  class SiteHeader extends HTMLElement {
    connectedCallback() {
      this.update = () => {
        theme.headerHeight = this.offsetHeight;
        document.documentElement.style.setProperty('--header-h', `${this.offsetHeight}px`);
      };
      this.update();
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.update);
        this.resizeObserver.observe(this);
      }
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }
  }

  /* ---------------------------------------------------------------------
     Barre promotionnelle : pause du défilement d'un appui
     --------------------------------------------------------------------- */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.marquee = this.querySelector('[data-marquee]');
      if (!this.marquee) return;
      // Sans bouton visible : un appui sur les messages (hors lien) met le
      // défilement en pause ou le relance. Le survol et le focus le suspendent aussi.
      this.onToggle = (event) => {
        if (event.target instanceof Element && event.target.closest('a')) return;
        this.classList.toggle('is-paused');
      };
      this.marquee.addEventListener('click', this.onToggle);
    }

    disconnectedCallback() {
      if (this.marquee) this.marquee.removeEventListener('click', this.onToggle);
    }
  }

  /* ---------------------------------------------------------------------
     Vidéo d'entrée : lecture automatique respectueuse + bouton pause
     --------------------------------------------------------------------- */
  class HeroVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.button = this.querySelector('[data-video-toggle]');
      if (!this.video) return;

      if (reduceMotion.matches) {
        this.video.removeAttribute('autoplay');
        this.video.pause();
      }

      this.sync = () => {
        const paused = this.video.paused;
        this.classList.toggle('is-paused', paused);
        if (this.button) {
          this.button.setAttribute('aria-label', paused ? this.button.dataset.labelPlay : this.button.dataset.labelPause);
        }
      };
      this.video.addEventListener('play', this.sync);
      this.video.addEventListener('pause', this.sync);
      this.sync();

      if (this.button) {
        this.button.addEventListener('click', () => {
          if (this.video.paused) this.video.play().catch(() => {});
          else this.video.pause();
        });
      }
    }
  }

  /* ---------------------------------------------------------------------
     Quantité : boutons − / +, valeurs invalides corrigées, bornes respectées
     Les bornes (min, max, pas) viennent des attributs du champ, eux-mêmes
     issus de Shopify (règles de quantité, stock disponible).
     --------------------------------------------------------------------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input) return;
      this.minus = this.querySelector('[data-minus]');
      this.plus = this.querySelector('[data-plus]');
      this.hint = this.parentElement ? this.parentElement.querySelector('[data-quantity-hint]') : null;

      this.onClick = (event) => {
        const button = event.target instanceof Element ? event.target.closest('button') : null;
        if (!button || button.disabled) return;
        const { min, max, step } = this.bounds();
        const current = parseInt(this.input.value, 10) || 0;
        const next = Math.min(max, Math.max(min, current + (button.hasAttribute('data-plus') ? step : -step)));
        if (next === current) return;
        this.input.value = String(next);
        this.setHint(next >= max && Number.isFinite(max) ? this.text('max', max) : '');
        this.sync();
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      };

      // S'exécute avant les écouteurs placés plus haut (panier, fiche produit)
      this.onChange = () => this.sanitize(true);

      this.onKeydown = (event) => {
        if (['e', 'E', '+', '-', '.', ','].includes(event.key)) event.preventDefault();
      };

      this.addEventListener('click', this.onClick);
      this.input.addEventListener('change', this.onChange);
      this.input.addEventListener('keydown', this.onKeydown);
      this.sync();
    }

    disconnectedCallback() {
      if (!this.input) return;
      this.removeEventListener('click', this.onClick);
      this.input.removeEventListener('change', this.onChange);
      this.input.removeEventListener('keydown', this.onKeydown);
    }

    bounds() {
      return {
        min: this.input.min !== '' ? Number(this.input.min) : 0,
        max: this.input.max !== '' ? Number(this.input.max) : Infinity,
        step: Math.max(1, Number(this.input.step) || 1),
      };
    }

    text(kind, value) {
      if (!this.hint) return '';
      const template = kind === 'max' ? this.hint.dataset.maxText : this.hint.dataset.minText;
      return (template || '').replace(`[${kind}]`, String(value));
    }

    setHint(message) {
      if (!this.hint) return;
      this.hint.textContent = message;
      this.hint.hidden = !message;
    }

    /* Ramène la valeur à un entier valide, dans les bornes et sur le pas */
    sanitize(announce) {
      const { min, max, step } = this.bounds();
      let value = parseInt(this.input.value, 10);
      let message = '';

      if (Number.isNaN(value)) value = parseInt(this.input.defaultValue, 10) || min;
      if (step > 1) value = min + Math.round((value - min) / step) * step;
      if (value < min) {
        value = min;
        message = this.text('min', min);
      }
      if (value > max) {
        value = max;
        message = this.text('max', max);
      }

      this.input.value = String(value);
      if (announce) this.setHint(message);
      this.sync();
      return value;
    }

    sync() {
      const { min, max } = this.bounds();
      const value = parseInt(this.input.value, 10) || 0;
      if (this.minus) this.minus.disabled = value <= min;
      if (this.plus) this.plus.disabled = value >= max;
    }

    /* Appelé par la fiche produit quand la variante change */
    setBounds({ min, max, step }) {
      this.input.min = String(min || 1);
      this.input.step = String(step || 1);
      if (max === null || max === undefined) this.input.removeAttribute('max');
      else this.input.max = String(max);
      const before = parseInt(this.input.value, 10);
      const after = this.sanitize(false);
      const limit = this.bounds().max;
      this.setHint(after !== before && Number.isFinite(limit) && after >= limit ? this.text('max', after) : '');
    }
  }

  /* ---------------------------------------------------------------------
     Galerie produit : défilement aimanté, vignettes, compteur et zoom
     --------------------------------------------------------------------- */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      this.slides = Array.from(this.track.children);
      this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
      this.counter = this.querySelector('[data-current]');
      this.dots = Array.from(this.querySelectorAll('[data-dot]'));
      this.prevButton = this.querySelector('[data-gallery-prev]');
      this.nextButton = this.querySelector('[data-gallery-next]');
      this.thumbList = this.querySelector('.gallery__thumbs');
      this.scrollLock = 0;
      this.zoomButtons = Array.from(this.querySelectorAll('[data-zoom]'));
      this.lightbox = this.querySelector('[data-lightbox]');
      this.index = 0;
      this.lightboxIndex = 0;

      this.onClick = (event) => {
        const target = event.target instanceof Element ? event.target : null;
        if (!target) return;

        const thumb = target.closest('[data-thumb]');
        if (thumb) {
          this.show(thumb.dataset.thumb);
          return;
        }

        if (target.closest('[data-gallery-prev]')) {
          this.showIndex(this.index - 1);
          return;
        }
        if (target.closest('[data-gallery-next]')) {
          this.showIndex(this.index + 1);
          return;
        }

        // Bouton « Agrandir » : ouvre l'image affichée
        if (target.closest('[data-gallery-zoom]')) {
          const slide = this.slides[this.index];
          const current = slide ? slide.querySelector('[data-zoom]') : null;
          this.openLightbox(Math.max(0, this.zoomButtons.indexOf(current)));
          return;
        }

        const zoom = target.closest('[data-zoom]');
        if (zoom) {
          this.openLightbox(this.zoomButtons.indexOf(zoom));
          return;
        }

        if (target.closest('[data-lightbox-prev]')) {
          this.stepLightbox(-1);
          return;
        }
        if (target.closest('[data-lightbox-next]')) {
          this.stepLightbox(1);
          return;
        }
        if (target.closest('[data-lightbox-zoom]')) {
          this.setZoom(!this.isZoomed());
          return;
        }

        const image = target.closest('[data-lightbox-image]');
        if (image) {
          const rect = image.getBoundingClientRect();
          this.setZoom(!this.isZoomed(), (event.clientX - rect.left) / rect.width, (event.clientY - rect.top) / rect.height);
        }
      };
      this.addEventListener('click', this.onClick);

      this.onScroll = () => {
        // Pendant un défilement lancé par une flèche ou une miniature, l'image visée reste active
        if (Date.now() < this.scrollLock) return;
        const width = this.track.clientWidth || 1;
        this.setActive(Math.round(Math.abs(this.track.scrollLeft) / width));
      };
      this.track.addEventListener('scroll', this.onScroll, { passive: true });

      this.onKeydown = (event) => {
        if (this.lightbox && this.lightbox.open) {
          if (this.isZoomed()) return;
          if (event.key === 'ArrowRight') this.stepLightbox(1);
          else if (event.key === 'ArrowLeft') this.stepLightbox(-1);
          else return;
          event.preventDefault();
          return;
        }
        if (event.target !== this.track) return;
        if (event.key === 'ArrowRight') this.showIndex(this.index + 1);
        else if (event.key === 'ArrowLeft') this.showIndex(this.index - 1);
        else return;
        event.preventDefault();
      };
      this.addEventListener('keydown', this.onKeydown);

      // Dès que le visiteur approche la galerie, les images suivantes sont
      // chargées pour que le défilement ne montre jamais de cadre vide.
      this.onIntent = () => {
        this.track.querySelectorAll('img[loading="lazy"]').forEach((image) => {
          image.loading = 'eager';
        });
      };
      ['pointerenter', 'touchstart', 'focusin'].forEach((type) => {
        this.addEventListener(type, this.onIntent, { once: true, passive: true });
      });

      this.syncControls();

      if (this.lightbox) {
        // À la fermeture, la galerie se cale sur l'image qui était agrandie
        this.onLightboxClose = () => {
          const button = this.zoomButtons[this.lightboxIndex];
          const slide = button ? button.closest('[data-media-id]') : null;
          if (slide) this.show(slide.dataset.mediaId, true);
        };
        this.lightbox.addEventListener('close', this.onLightboxClose);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
      if (this.track) this.track.removeEventListener('scroll', this.onScroll);
      if (this.lightbox && this.onLightboxClose) this.lightbox.removeEventListener('close', this.onLightboxClose);
    }

    show(mediaId, instant = false) {
      if (!this.track) return;
      const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (index >= 0) this.showIndex(index, instant);
    }

    showIndex(index, instant = false) {
      const target = Math.max(0, Math.min(this.slides.length - 1, index));
      const slide = this.slides[target];
      if (!slide) return;
      const immediate = instant || reduceMotion.matches;
      this.scrollLock = immediate ? 0 : Date.now() + 600;
      this.setActive(target);
      this.track.scrollTo({
        left: slide.offsetLeft,
        behavior: instant || reduceMotion.matches ? 'auto' : 'smooth',
      });
    }

    setActive(index) {
      if (index === this.index || !this.slides[index]) return;
      this.index = index;
      if (this.counter) this.counter.textContent = String(index + 1);
      this.thumbs.forEach((thumb, position) => {
        if (position === index) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      this.syncControls();
      // Met en pause les vidéos qui ne sont plus à l'écran
      this.slides.forEach((slide, position) => {
        if (position === index) return;
        slide.querySelectorAll('video').forEach((video) => video.pause());
      });
    }

    /* Points de position, flèches et rail de miniatures */
    syncControls() {
      this.dots.forEach((dot, position) => dot.classList.toggle('is-active', position === this.index));
      if (this.prevButton) this.prevButton.disabled = this.index <= 0;
      if (this.nextButton) this.nextButton.disabled = this.index >= this.slides.length - 1;

      // Garde la miniature active visible dans son rail, sans faire défiler la page
      const list = this.thumbList;
      const item = this.thumbs[this.index] ? this.thumbs[this.index].parentElement : null;
      if (!list || !item) return;
      if (item.offsetTop < list.scrollTop) list.scrollTop = item.offsetTop;
      else if (item.offsetTop + item.offsetHeight > list.scrollTop + list.clientHeight) {
        list.scrollTop = item.offsetTop + item.offsetHeight - list.clientHeight;
      }
      if (item.offsetLeft < list.scrollLeft) list.scrollLeft = item.offsetLeft;
      else if (item.offsetLeft + item.offsetWidth > list.scrollLeft + list.clientWidth) {
        list.scrollLeft = item.offsetLeft + item.offsetWidth - list.clientWidth;
      }
    }

    /* --- Zoom plein écran --- */
    isZoomed() {
      return Boolean(this.lightbox && this.lightbox.classList.contains('is-zoomed'));
    }

    openLightbox(position) {
      if (!this.lightbox || position < 0 || typeof this.lightbox.showModal !== 'function') return;
      this.lightboxIndex = position;
      this.renderLightbox();
      theme.openDialog(this.lightbox);
    }

    stepLightbox(delta) {
      const count = this.zoomButtons.length;
      if (count < 2) return;
      this.lightboxIndex = (this.lightboxIndex + delta + count) % count;
      this.renderLightbox();
    }

    renderLightbox() {
      const button = this.zoomButtons[this.lightboxIndex];
      const image = this.lightbox.querySelector('[data-lightbox-image]');
      const count = this.lightbox.querySelector('[data-lightbox-count]');
      if (!button || !image) return;

      this.setZoom(false);
      image.src = button.dataset.zoomSrc;
      image.alt = button.dataset.zoomAlt || '';
      if (button.dataset.zoomWidth) image.width = Number(button.dataset.zoomWidth);
      if (button.dataset.zoomHeight) image.height = Number(button.dataset.zoomHeight);
      if (count) {
        count.textContent = (count.dataset.template || '')
          .replace('[index]', String(this.lightboxIndex + 1))
          .replace('[count]', String(this.zoomButtons.length));
      }
    }

    setZoom(zoomed, originX = 0.5, originY = 0.5) {
      if (!this.lightbox) return;
      const viewport = this.lightbox.querySelector('[data-lightbox-viewport]');
      const toggle = this.lightbox.querySelector('[data-lightbox-zoom]');
      this.lightbox.classList.toggle('is-zoomed', zoomed);
      if (toggle) toggle.setAttribute('aria-pressed', String(zoomed));
      if (!viewport) return;
      if (zoomed) {
        // Centre la zone agrandie sur le point cliqué ; le doigt ou la souris font ensuite défiler
        viewport.scrollLeft = (viewport.scrollWidth - viewport.clientWidth) * Math.min(1, Math.max(0, originX));
        viewport.scrollTop = (viewport.scrollHeight - viewport.clientHeight) * Math.min(1, Math.max(0, originY));
      } else {
        viewport.scrollLeft = 0;
        viewport.scrollTop = 0;
      }
    }
  }

  /* ---------------------------------------------------------------------
     Panier : rendu des sections renvoyées par Shopify
     --------------------------------------------------------------------- */
  theme.cartSectionIds = () =>
    Array.from(document.querySelectorAll('cart-items[data-section]')).map((element) => element.dataset.section);

  theme.renderCart = (sections) => {
    if (!sections) return false;
    let rendered = false;

    Object.keys(sections).forEach((id) => {
      const html = sections[id];
      const wrapper = document.getElementById(`shopify-section-${id}`);
      if (!html || !wrapper) return;

      const current = wrapper.querySelector('[data-cart-contents]');
      const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-contents]');
      if (!current || !next) return;

      current.innerHTML = next.innerHTML;
      current.dataset.cartCountValue = next.dataset.cartCountValue;
      theme.updateCartCount(Number(next.dataset.cartCountValue) || 0);
      rendered = true;
    });

    return rendered;
  };

  theme.updateCartCount = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = String(count);
      element.hidden = count === 0;
    });
  };

  const readError = (data) => {
    if (data && typeof data.description === 'string' && data.description) return data.description;
    if (data && typeof data.message === 'string' && data.message) return data.message;
    return theme.strings.cartError || '';
  };

  class CartItems extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.status = this.querySelector('[data-cart-status]');

      this.onChange = (event) => {
        const input = event.target instanceof Element ? event.target.closest('[data-line-qty]') : null;
        if (!input) return;
        const line = input.closest('[data-line]');
        const quantity = Math.max(0, parseInt(input.value, 10) || 0);
        if (line) this.updateLine(Number(line.dataset.line), quantity, 'qty', input);
      };

      this.onClick = (event) => {
        const remove = event.target instanceof Element ? event.target.closest('[data-remove]') : null;
        if (!remove) return;
        const line = remove.closest('[data-line]');
        if (!line) return;
        event.preventDefault();
        this.updateLine(Number(line.dataset.line), 0, 'remove');
      };

      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onClick);
    }

    showError(message) {
      const error = this.querySelector('[data-cart-error]');
      if (!error) return;
      error.textContent = message;
      error.hidden = !message;
    }

    resetInput(input) {
      if (!input) return;
      input.value = input.defaultValue;
      const wrapper = input.closest('quantity-input');
      if (wrapper && typeof wrapper.sync === 'function') wrapper.sync();
    }

    async updateLine(line, quantity, focusKey, input) {
      if (this.classList.contains('is-loading')) {
        this.resetInput(input);
        return;
      }
      this.classList.add('is-loading');
      this.setAttribute('aria-busy', 'true');
      this.showError('');

      try {
        const response = await fetch(`${theme.routes.cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            line,
            quantity,
            sections: theme.cartSectionIds(),
            sections_url: window.location.pathname,
          }),
        });
        const data = await response.json();

        if (!response.ok || data.status) {
          this.resetInput(input);
          this.showError(readError(data));
          return;
        }

        if (!theme.renderCart(data.sections)) {
          window.location.reload();
          return;
        }

        if (this.status) this.status.textContent = theme.strings.cartUpdated || '';

        // Replace le focus au plus près de l'élément manipulé
        const target =
          this.querySelector(`[data-line="${line}"] [data-focus="${focusKey}"]`) ||
          this.querySelector('[data-line] [data-focus="qty"]') ||
          this;
        target.focus();
      } catch (error) {
        this.resetInput(input);
        this.showError(theme.strings.cartError || '');
      } finally {
        this.classList.remove('is-loading');
        this.removeAttribute('aria-busy');
      }
    }
  }

  /* ---------------------------------------------------------------------
     Produit : variantes, prix, quantité, ajout au panier, barre d'achat fixe
     --------------------------------------------------------------------- */
  class ProductInfo extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form[data-product-form]');
      this.idInput = this.form ? this.form.querySelector('[data-variant-id]') : null;
      this.gallery = this.querySelector('media-gallery');
      this.quantity = this.querySelector('quantity-input');
      this.optionGroups = Array.from(this.querySelectorAll('[data-option]'));
      this.status = this.querySelector('[data-variant-status]');
      this.updateUrl = this.dataset.updateUrl === 'true';

      const source = this.querySelector('script[data-variants]');
      try {
        this.variants = source ? JSON.parse(source.textContent) : [];
      } catch (error) {
        this.variants = [];
      }

      this.onOptionChange = (event) => {
        if (event.target instanceof Element && event.target.closest('[data-option]')) this.onVariantChange();
      };
      this.addEventListener('change', this.onOptionChange);

      if (this.form) {
        this.onSubmit = (event) => this.addToCart(event);
        this.form.addEventListener('submit', this.onSubmit);
      }

      if (this.optionGroups.length) {
        const options = this.selectedOptions();
        const variant = this.findVariant(options);
        this.markAvailability(options);
        this.renderStatus(variant);
        if (variant && variant.media_id && this.gallery && typeof this.gallery.show === 'function') {
          this.gallery.show(variant.media_id, true);
        }
      }

      this.initStickyBar();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onOptionChange);
      if (this.form && this.onSubmit) this.form.removeEventListener('submit', this.onSubmit);
      clearTimeout(this.successTimer);
      if (this.updateSticky) {
        window.removeEventListener('scroll', this.updateSticky);
        window.removeEventListener('resize', this.updateSticky);
      }
      if (this.stickyResize) this.stickyResize.disconnect();
      if (this.stickyBar) document.body.style.removeProperty('--sticky-buy-h');
    }

    selectedOptions() {
      return this.optionGroups.map((group) => {
        const checked = group.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    findVariant(options) {
      return this.variants.find((variant) => variant.options.every((value, index) => value === options[index])) || null;
    }

    currentVariant() {
      if (!this.idInput || !this.idInput.value) return null;
      return this.variants.find((variant) => String(variant.id) === this.idInput.value) || null;
    }

    onVariantChange() {
      const options = this.selectedOptions();
      const variant = this.findVariant(options);

      this.optionGroups.forEach((group, index) => {
        const label = group.querySelector('[data-option-selected]');
        if (label) label.textContent = options[index] || '';
      });

      this.markAvailability(options);
      this.renderVariant(variant);
      this.renderStatus(variant);

      if (variant && variant.media_id && this.gallery && typeof this.gallery.show === 'function') {
        this.gallery.show(variant.media_id);
      }

      if (variant && this.updateUrl && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    /* Signale les valeurs qui mènent à une combinaison épuisée ou inexistante */
    markAvailability(options) {
      this.optionGroups.forEach((group, index) => {
        group.querySelectorAll('input').forEach((input) => {
          const candidate = options.slice();
          candidate[index] = input.value;
          const variant = this.findVariant(candidate);
          const unavailable = !variant || !variant.available;
          const label = group.querySelector(`label[for="${input.id}"]`);
          if (!label) return;
          label.classList.toggle('is-unavailable', unavailable);
          const text = label.querySelector('[data-unavailable-text]');
          if (text) text.hidden = !unavailable;
        });
      });
    }

    /* Message visible expliquant pourquoi l'achat est impossible */
    renderStatus(variant) {
      if (!this.status) return;
      let message = '';
      if (!variant) message = this.status.dataset.unavailable || '';
      else if (!variant.available) message = this.status.dataset.soldOut || '';
      this.status.textContent = message;
      this.status.hidden = !message;
    }

    /* Libellé et disponibilité des boutons d'ajout (formulaire + barre fixe) */
    updateButtons(variant) {
      clearTimeout(this.successTimer);
      const available = Boolean(variant && variant.available);
      const label = !variant ? theme.strings.unavailable : available ? theme.strings.addToCart : theme.strings.soldOut;
      this.querySelectorAll('[data-add-button]').forEach((button) => {
        theme.setButtonState(button, null);
        button.disabled = !available;
        const text = button.querySelector('[data-add-label]');
        if (text) text.textContent = label;
      });
    }

    renderVariant(variant) {
      const available = Boolean(variant && variant.available);

      if (this.idInput) {
        this.idInput.value = variant ? variant.id : '';
        this.idInput.disabled = !variant;
        // Prévient Shopify (bouton d'achat dynamique, applications) du changement
        this.idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      this.updateButtons(variant);

      const dynamic = this.querySelector('[data-dynamic-checkout]');
      if (dynamic) dynamic.hidden = !available;

      this.showError('');
      if (!variant) return;

      // La barre d'achat fixe reprend la variante choisie
      const stickyVariant = this.querySelector('[data-sticky-variant]');
      if (stickyVariant && this.optionGroups.length) stickyVariant.textContent = variant.title || '';

      // Quantité : bornes de la nouvelle variante (règles Shopify, stock disponible)
      if (this.quantity && typeof this.quantity.setBounds === 'function') {
        this.quantity.setBounds({ min: variant.min, max: variant.max, step: variant.step });
      }

      // Les prix sont déjà formatés par Shopify dans la devise du visiteur.
      const onSale = Boolean(variant.compare);
      this.querySelectorAll('[data-price]').forEach((price) => {
        price.classList.toggle('price--sale', onSale);
        const current = price.querySelector('[data-price-current]');
        const compare = price.querySelector('[data-price-compare]');
        const compareValue = price.querySelector('[data-price-compare-value]');
        const saleLabel = price.querySelector('[data-price-label-sale]');
        const badge = price.querySelector('[data-sale-badge]');
        if (current) current.innerHTML = variant.price;
        if (compareValue) compareValue.innerHTML = onSale ? variant.compare : '';
        if (compare) compare.hidden = !onSale;
        if (saleLabel) saleLabel.hidden = !onSale;
        if (badge) badge.hidden = !onSale;
      });
    }

    /* Confirmation visible quelques instants après un ajout réussi */
    confirmAdded() {
      clearTimeout(this.successTimer);
      this.querySelectorAll('[data-add-button]').forEach((button) => {
        theme.setButtonState(button, 'success');
        const text = button.querySelector('[data-add-label]');
        if (text && theme.strings.added) text.textContent = theme.strings.added;
      });
      this.successTimer = setTimeout(() => this.updateButtons(this.currentVariant()), 2400);
    }

    showError(message) {
      const error = this.querySelector('[data-form-error]');
      if (!error) return;
      error.textContent = message;
      error.hidden = !message;
      if (message) error.scrollIntoView({ block: 'center', behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }

    setBusy(busy) {
      this.busy = busy;
      this.querySelectorAll('[data-add-button]').forEach((button) => {
        theme.setButtonState(button, busy ? 'loading' : null);
      });
    }

    async addToCart(event) {
      // Le bouton d'achat dynamique gère lui-même son envoi.
      if (event.submitter && !event.submitter.hasAttribute('data-add-button')) return;
      event.preventDefault();
      // Un envoi est déjà en cours : les clics supplémentaires sont ignorés
      if (this.busy) return;

      if (this.quantity && typeof this.quantity.sanitize === 'function') this.quantity.sanitize(true);

      this.setBusy(true);
      this.showError('');
      let added = false;

      const body = new FormData(this.form);
      const sections = theme.cartSectionIds();
      if (sections.length) {
        body.append('sections', sections.join(','));
        body.append('sections_url', window.location.pathname);
      }

      try {
        const response = await fetch(`${theme.routes.cartAdd}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body,
        });
        const data = await response.json();

        if (!response.ok || data.status) {
          this.showError(readError(data));
          return;
        }

        added = true;
        const drawer = document.getElementById('CartDrawer');
        if (!drawer || !theme.renderCart(data.sections)) {
          window.location.href = theme.routes.cart;
          return;
        }
        theme.openDialog(drawer);
      } catch (error) {
        this.showError(theme.strings.cartError || '');
      } finally {
        this.setBusy(false);
        if (added) this.confirmAdded();
      }
    }

    /* Barre d'achat fixe (téléphone) : visible seulement une fois le bouton
       principal dépassé, c'est-à-dire sorti de l'écran par le haut. Sa hauteur
       est réservée en bas de page pour ne masquer aucun contenu. */
    initStickyBar() {
      const bar = this.querySelector('[data-sticky-bar]');
      const mainButton = this.form ? this.form.querySelector('[data-add-button]') : null;
      if (!bar || !mainButton) return;

      this.stickyBar = bar;
      bar.hidden = false;
      bar.inert = true;

      const reserve = () => document.body.style.setProperty('--sticky-buy-h', `${bar.offsetHeight}px`);
      reserve();
      if ('ResizeObserver' in window) {
        this.stickyResize = new ResizeObserver(reserve);
        this.stickyResize.observe(bar);
      }

      this.updateSticky = () => {
        const passed = mainButton.getBoundingClientRect().bottom < theme.headerHeight;
        if (passed === bar.classList.contains('is-active')) return;
        bar.classList.toggle('is-active', passed);
        bar.inert = !passed;
      };
      window.addEventListener('scroll', this.updateSticky, { passive: true });
      window.addEventListener('resize', this.updateSticky);
      this.updateSticky();
    }
  }

  /* ---------------------------------------------------------------------
     Code promotionnel : copie dans le presse-papiers
     --------------------------------------------------------------------- */
  class CopyCode extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-copy]');
      this.status = this.querySelector('[data-status]');
      const code = this.querySelector('[data-code]');
      if (!this.button || !code) return;

      if (!navigator.clipboard || typeof navigator.clipboard.writeText !== 'function') {
        this.button.hidden = true;
        return;
      }

      this.button.addEventListener('click', async () => {
        try {
          await navigator.clipboard.writeText(code.textContent.trim());
          theme.setButtonState(this.button, 'success');
          if (this.status) this.status.textContent = this.status.dataset.copied || '';
          clearTimeout(this.timer);
          this.timer = setTimeout(() => {
            theme.setButtonState(this.button, null);
            if (this.status) this.status.textContent = '';
          }, 3000);
        } catch (error) {
          /* Copie refusée par le navigateur : le code reste lisible et sélectionnable. */
        }
      });
    }
  }

  /* ---------------------------------------------------------------------
     Compte à rebours de la barre promotionnelle
     L'échéance est un instant absolu calculé par Shopify (attribut data-end,
     en secondes) : identique pour tous les visiteurs, et le chrono ne repart
     jamais de zéro au rechargement. À l'expiration, l'offre est masquée ou
     remplacée par le message de fin, selon le réglage du bloc.
     --------------------------------------------------------------------- */
  class CountDown extends HTMLElement {
    connectedCallback() {
      this.end = Number(this.dataset.end) * 1000;
      this.timerElement = this.querySelector('[data-timer]');
      this.fallback = this.querySelector('[data-fallback]');
      this.parts = {};
      ['days', 'hours', 'minutes', 'seconds'].forEach((name) => {
        const part = this.querySelector(`[data-part="${name}"]`);
        if (part) this.parts[name] = { part, value: part.querySelector('[data-value]') };
      });
      if (!this.end || !this.timerElement) return;

      this.timerElement.hidden = false;
      // La date de fin en clair reste disponible pour les lecteurs d'écran
      if (this.fallback) this.fallback.classList.add('visually-hidden');
      this.tick = this.tick.bind(this);
      this.tick();
      this.interval = setInterval(this.tick, 1000);
    }

    disconnectedCallback() {
      clearInterval(this.interval);
    }

    setPart(name, value, visible, pad) {
      const entry = this.parts[name];
      if (!entry) return;
      entry.part.hidden = !visible;
      const text = pad ? String(value).padStart(2, '0') : String(value);
      if (entry.value && entry.value.textContent !== text) entry.value.textContent = text;
    }

    tick() {
      const left = Math.floor((this.end - Date.now()) / 1000);
      if (left <= 0) {
        this.expire();
        return;
      }
      const days = Math.floor(left / 86400);
      const hours = Math.floor((left % 86400) / 3600);
      const minutes = Math.floor((left % 3600) / 60);
      const seconds = left % 60;
      // Unités affichées selon la durée restante
      this.setPart('days', days, days > 0, false);
      this.setPart('hours', hours, days > 0 || hours > 0, true);
      this.setPart('minutes', minutes, days > 0 || hours > 0 || minutes > 0, true);
      this.setPart('seconds', seconds, days === 0, true);
    }

    expire() {
      clearInterval(this.interval);
      const text = this.querySelector('[data-expired-text]');
      if (this.dataset.expired === 'message' && text && text.textContent.trim()) {
        this.timerElement.hidden = true;
        if (this.fallback) this.fallback.hidden = true;
        const label = this.querySelector('[data-countdown-label]');
        if (label) label.hidden = true;
        text.hidden = false;
        this.classList.add('countdown--expired');
        return;
      }

      // Offre masquée ; la barre disparaît si plus rien n'y est affiché
      this.hidden = true;
      const pinned = this.closest('.announcement__pinned');
      if (pinned && !pinned.querySelector('.countdown:not([hidden])')) pinned.hidden = true;
      const bar = this.closest('announcement-bar');
      if (bar && !bar.querySelector('.announcement__messages') && (!pinned || pinned.hidden)) bar.hidden = true;
    }
  }

  define('count-down', CountDown);
  define('site-header', SiteHeader);
  define('announcement-bar', AnnouncementBar);
  define('hero-video', HeroVideo);
  define('quantity-input', QuantityInput);
  define('media-gallery', MediaGallery);
  define('cart-items', CartItems);
  define('product-info', ProductInfo);
  define('copy-code', CopyCode);

  /* ---------------------------------------------------------------------
     Initialisation + événements de l'éditeur de thème Shopify
     --------------------------------------------------------------------- */
  const init = () => {
    theme.initForms();
    theme.initReveal();
    if (!inDesignMode()) theme.focusFormFeedback();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  document.addEventListener('shopify:section:load', (event) => {
    theme.initForms(event.target);
    theme.initReveal(event.target);
  });

  document.addEventListener('shopify:section:select', (event) => {
    const drawer = event.target.querySelector('#CartDrawer');
    if (drawer) theme.openDialog(drawer);
  });

  document.addEventListener('shopify:section:deselect', (event) => {
    const drawer = event.target.querySelector('#CartDrawer');
    if (drawer && drawer.open) drawer.close();
  });

  document.addEventListener('shopify:block:select', (event) => {
    const details = event.target.closest('details');
    if (details) details.open = true;
  });
})();
